import React, { useMemo } from "react";
import { Typography, Box } from "@mui/material";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

import "./HomeConsensusGraph.css";

const LINE_COLORS = [
  "#ef767a",
  "#49beaa",
  "#7c8cff",
  "#fcc419",
  "#ab7ee4",
  "#5ad4e6",
  "#f4904a",
  "#88c070",
  "#e85d75",
  "#61dafb",
];

function dateFormatter(tickItem) {
  return format(new Date(tickItem), "MMM yyyy");
}

function scoreKey(playerId) {
  return `s_${playerId}`;
}

function rankKey(playerId) {
  return `r_${playerId}`;
}

function mapSeries(consensusSeries) {
  return (consensusSeries || [])
    .map((pt) => ({
      effective_date: new Date(pt.effective_date).getTime(),
      score:
        pt.score != null
          ? Math.round(Number(pt.score) * 1e2) / 1e2
          : null,
      rank: pt.rank != null ? Number(pt.rank) : null,
    }))
    .filter(
      (row) =>
        Number.isFinite(row.effective_date) && row.score != null
    );
}

/**
 * One shared `data` array so the cursor / tooltip align on `effective_date`.
 * (Separate `data` per Line makes Recharts match by row index, which skews values.)
 */
function buildMergedModel(players) {
  const seriesByPlayer = players
    .map((p) => ({
      player: p,
      data: mapSeries(p.consensus_series),
    }))
    .filter(({ data }) => data.length > 0);

  if (!seriesByPlayer.length) {
    return null;
  }

  const dateMap = new Map();
  for (const { player, data } of seriesByPlayer) {
    const sk = scoreKey(player.player_id);
    const rk = rankKey(player.player_id);
    for (const pt of data) {
      const ts = pt.effective_date;
      if (!dateMap.has(ts)) {
        dateMap.set(ts, { effective_date: ts });
      }
      const row = dateMap.get(ts);
      row[sk] = pt.score;
      row[rk] = pt.rank;
    }
  }

  const mergedData = Array.from(dateMap.values()).sort(
    (a, b) => a.effective_date - b.effective_date
  );

  const lineMeta = seriesByPlayer.map(({ player }, i) => ({
    playerId: player.player_id,
    dataKey: scoreKey(player.player_id),
    name: `${player.first_name} ${player.last_name}`.trim(),
    stroke: LINE_COLORS[i % LINE_COLORS.length],
  }));

  return { mergedData, lineMeta };
}

function HomeConsensusTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;
  if (!row || row.effective_date == null) {
    return null;
  }

  const ts = row.effective_date;
  const dateLabel = format(new Date(ts), "MMM d, yyyy");

  const entries = payload
    .filter(
      (p) =>
        p &&
        typeof p.dataKey === "string" &&
        p.dataKey.startsWith("s_") &&
        p.value != null &&
        !Number.isNaN(Number(p.value))
    )
    .map((p) => {
      const id = p.dataKey.slice(2);
      const rank = row[rankKey(id)];
      return {
        name: p.name,
        color: p.color,
        score: Number(p.value),
        rank: rank != null && !Number.isNaN(Number(rank)) ? Number(rank) : null,
      };
    })
    .sort((a, b) => {
      if (a.rank != null && b.rank != null) {
        return a.rank - b.rank;
      }
      if (a.rank != null) {
        return -1;
      }
      if (b.rank != null) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });

  if (!entries.length) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: "rgba(22, 22, 24, 0.96)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 6,
        padding: "10px 12px",
        fontSize: 12,
        minWidth: 200,
        maxWidth: 320,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, color: "#eee" }}>
        {dateLabel}
      </div>
      {entries.map((e) => (
        <div
          key={e.name}
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 4,
            color: "#ccc",
          }}
        >
          <span style={{ color: e.color || "#ccc", flex: "1 1 auto" }}>
            {e.name}
          </span>
          <span style={{ whiteSpace: "nowrap", color: "#fafafa" }}>
            score {e.score}
            {e.rank != null ? ` · rank ${e.rank}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HomeConsensusGraph({
  loading,
  payload,
  compact = false,
  embedded = false,
}) {
  const players = payload?.players ?? [];

  const chartModel = useMemo(
    () => buildMergedModel(players),
    [players]
  );

  if (loading) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Loading consensus chart…
        </Typography>
      </Box>
    );
  }

  if (!chartModel) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No consensus history for top prospects yet.
        </Typography>
      </Box>
    );
  }

  const { mergedData, lineMeta } = chartModel;
  const plotHeight = compact ? 168 : 220;

  return (
    <div className={`home-consensus-chart${embedded ? " home-consensus-chart--embedded" : ""}`}>
      {!embedded && (
        <Typography
          variant="h4"
          component="div"
          className="mini-title"
          sx={{ mb: 1 }}
        >
          Top prospects — ranking score over time
        </Typography>
      )}
      <div className="home-consensus-chart-plot" style={{ height: plotHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={mergedData}
            margin={{ top: 6, right: 12, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="1" stroke="#333" />
            <XAxis
              type="number"
              dataKey="effective_date"
              domain={["dataMin", "dataMax"]}
              tickFormatter={dateFormatter}
              stroke="#999"
              interval="preserveStartEnd"
              tick={{ fontSize: 10 }}
              tickCount={5}
              height={24}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              reversed
              tickCount={6}
              domain={[1, "dataMax + 10"]}
              stroke="#999"
              width={32}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              content={<HomeConsensusTooltip />}
              cursor={{ stroke: "rgba(255,255,255,0.25)", strokeWidth: 1 }}
              isAnimationActive={false}
            />
            {lineMeta.map((line) => (
              <Line
                key={line.playerId}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name}
                stroke={line.stroke}
                dot={false}
                strokeWidth={2}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="home-consensus-legend" aria-hidden="true">
        {lineMeta.map((line) => (
          <span key={line.playerId} className="home-consensus-legend-item" title={line.name}>
            <span
              className="home-consensus-legend-swatch"
              style={{ backgroundColor: line.stroke }}
            />
            <span className="home-consensus-legend-label">{line.name}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
